import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ConfigurationAlert } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, debounceTime } from 'rxjs';
import { NextServicio, PlanningArea, ServiceUserAssignment, Worker } from '../../modules/admin/services/services.types';
import { ServicesService } from '../../modules/admin/services/services.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'app-search-resources-dialog',
    templateUrl: './search-resources-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .sr-grid {
                grid-template-columns: 70px auto 80px 50px 84px;

                @screen sm {
                    grid-template-columns: 70px auto 80px 50px 84px;
                }

                @screen md {
                    grid-template-columns: 70px auto 144px 144px 80px 50px 90px;
                }

                @screen lg {
                    grid-template-columns: 80px auto 156px 156px 80px 50px 128px;
                }

                @screen xl {
                    grid-template-columns: 90px auto 170px 170px 80px 50px 128px;
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchResourcesDialogComponent implements OnInit {

    planningArea: PlanningArea;
    service: NextServicio;
    multiple: boolean;

    resourcesMap = new Map<'assigned' | 'unassigned', Worker[]>();
    usersAssigned: ServiceUserAssignment[];

    resources$: BehaviorSubject<Worker[]> = new BehaviorSubject([]);
    viewResources$: BehaviorSubject<Worker[]> = new BehaviorSubject([]);
    length: number;
    pageSize = 10;
    pageSizeOptions = [5, 10, 20];

    searchControl = new FormControl('');
    searchWithDNIControl = new FormControl();
    controlValidDNI = true;
    onlyNotAssociated = false;
    onlyAvailable = false;
    searchWithoutHrid = false;
    searchMyOdcOnly = false;
    hasChanges = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            planningArea: PlanningArea;
            service: NextServicio;
            usersAssigned: ServiceUserAssignment[];
            multiple: boolean;
        },
        public dialogRef: MatDialogRef<SearchResourcesDialogComponent>,
        private servicesService: ServicesService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
    ) { }

    get assigned() {
        if (!this.resourcesMap.get('assigned')) {
            this.resourcesMap.set('assigned', []);
        }
        return this.resourcesMap.get('assigned');
    }

    get unassigned() {
        if (!this.resourcesMap.get('unassigned')) {
            this.resourcesMap.set('unassigned', []);
        }
        return this.resourcesMap.get('unassigned');
    }

    ngOnInit() {
        this.service = this.data.service;
        this.planningArea = this.data.planningArea;
        this.usersAssigned = this.data.usersAssigned;
        this.multiple = this.data.multiple;
        this.searchResources();

        this.searchWithDNIControl.valueChanges.pipe(
            debounceTime(100),
        ).subscribe(() => {
            if (this.searchWithoutHrid) {
                this.searchControl.clearValidators();
                const validators: ValidatorFn[] = [];
                validators.push(Validators.pattern(/^[A-Za-z0-9]*\s*$/));
                if (validators.length > 0) {
                    this.searchControl.addValidators(validators);
                }
            } else {
                this.searchControl.clearValidators();
                this.controlValidDNI = true;
            }
        });

        this.searchControl.valueChanges.pipe(
            debounceTime(500),
        ).subscribe(() => {
            this.searchResources();
        });
    }

    async closeDialog() {
        this.dialogRef.close(this.resourcesMap);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.timestamp || index;
    }

    searchResources() {
        if (this.controlValidDNI) {
            this.servicesService.searchWorkers({
                expr: this.searchControl.value,
                onlyAvailable: this.onlyAvailable,
                onlyNotAssociated: this.onlyNotAssociated,
                serviceId: this.service.serviceId,
                planningArea: this.planningArea.planningArea,
                searchWithoutHrid: this.searchWithoutHrid,
                searchMyOdcOnly: this.searchMyOdcOnly
            }).subscribe((workers) => {
                for (const resource of workers) {
                    if (this.usersAssigned.find(worker => !worker.deprecated && worker.hrid === resource.hrid)
                        && !this.isAssigned(resource)) {
                        this.assigned.push(resource);
                    }
                }
                this.resources$.next(workers);
                this.viewResources$.next(workers.slice(0, this.pageSize));
                this.length = workers.length;
            });
        }
    }

    pageEventCallback(e: PageEvent) {
        this.pageSize = e.pageIndex - e.previousPageIndex === 0 ? e.pageSize : this.pageSize;
        const [startIndex, endIndex] = [e.pageIndex * e.pageSize, (e.pageIndex + 1) * e.pageSize];
        this.viewResources$.next(this.resources$.getValue().slice(startIndex, endIndex));
    }

    async toggleAssign(resource: Worker) {
        if (this.multiple) {
            if (!this.isAssigned(resource)) {
                this.assigned.push(resource);
                const index = this.unassigned.findIndex(res => resource.hrid === res.hrid);
                if (index > -1) {
                    this.unassigned.splice(index, 1);
                }
            } else {
                this.unassigned.push(resource);
                const index = this.assigned.findIndex(res => resource.hrid === res.hrid);
                if (index > -1) {
                    this.assigned.splice(index, 1);
                }
            }
        } else if (!this.multiple) {
            const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('assignResource', { name: resource.name }));
            if (confirm) {
                this.assigned.push(resource);
                const index = this.unassigned.findIndex(res => resource.hrid === res.hrid);
                if (index > -1) {
                    this.unassigned.splice(index, 1);
                }
                this.dialogRef.close(this.resourcesMap);
            }
        }
        this.hasChanges = true;
    }

    isAssigned(resource: Worker) {
        const assigned = this.resourcesMap.get('assigned');
        if (assigned) {
            const index = assigned.findIndex(res => resource.hrid === res.hrid);
            return index > -1;
        }
    }

    async assignResources() {
        this.dialogRef.close(this.resourcesMap);
    }

    chooseMajorIcon(resource: Worker) {
        let majorSvgIcon = '';
        if (resource.alert) {
            for (const alert of resource.alert) {
                if (alert.type === 'ERROR') {
                    majorSvgIcon = 'mat_outline:error_outline';
                } else if (alert.type === 'WARNING') {
                    if (majorSvgIcon !== 'mat_outline:error_outline') {
                        majorSvgIcon = 'mat_outline:warning_amber';
                    }
                } else {
                    if (majorSvgIcon !== 'mat_outline:error_outline' && majorSvgIcon !== 'mat_outline:warning_amber') {
                        majorSvgIcon = 'mat_outline:info';
                    }
                }
            }
        }
        return majorSvgIcon;
    }

    chooseAlertIcon(alert: ConfigurationAlert) {
        if (alert) {
            if (alert.type === 'ERROR') {
                return 'mat_outline:error_outline';
            } else if (alert.type === 'WARNING') {
                return 'mat_outline:warning_amber';
            } else {
                return 'mat_outline:info';
            }
        }
        return '';
    }

    toggleInfo(resource: Worker) {
        resource.showInfo = !resource.showInfo;
        if (resource.showInfo) {
            for (const r of this.resources$.value) {
                if (r.hrid !== resource.hrid) {
                    r.showInfo = false;
                }
            }
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

}
